import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';

export interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

interface FilterChipsBarProps {
  chips: FilterChip[];
  onClearAll: () => void;
}

/**
 * FilterChipsBar - presentational active-filter indicator (SSOT for Creator + Variations).
 * - Desktop: inline chips bar
 * - Mobile: compact icon + badge, tap to expand dropdown with chips
 */
export const FilterChipsBar = ({ chips: activeFilterChips, onClearAll: clearFilters }: FilterChipsBarProps) => {
  const { t } = useTranslation('common');
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileOpen]);

  // Don't render if no active filters
  if (activeFilterChips.length === 0) {
    return null;
  }

  // Chips list (shared between mobile dropdown and desktop inline)
  const chipsList = activeFilterChips.map((chip) => (
    <span
      key={chip.key}
      className="inline-flex items-center gap-1 px-2 py-1 bg-off-white text-dark-teal rounded-md text-xs font-medium"
    >
      {chip.label}
      <Tooltip content={t('remove_filter', { filter: chip.label })}>
        <button
          onClick={chip.onRemove}
          className="hover:bg-warm-gray rounded-full p-0.5 transition-colors"
          aria-label={t('remove_filter', { filter: chip.label })}
        >
          <Icon name={ICONS.CLOSE} size="xs" color="text-dark-teal" ariaHidden />
        </button>
      </Tooltip>
    </span>
  ));

  return (
    <>
      {/* Mobile: compact icon + badge, tap to expand */}
      <div className="lg:hidden relative" ref={dropdownRef}>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-soft-mint text-dark-teal rounded-lg border border-light-teal-gray text-xs font-medium"
          aria-label={t('show_active_filters')}
        >
          <Icon name={ICONS.FILTER} size="xs" color="text-dark-teal" ariaHidden />
          <span className="w-5 h-5 flex items-center justify-center bg-dark-teal text-white rounded-full text-[10px] font-bold">
            {activeFilterChips.length}
          </span>
        </button>

        {/* Dropdown */}
        {mobileOpen && (
          <div className="fixed left-4 right-4 top-[4.5rem] bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-700">
                {t('active_filters')} ({activeFilterChips.length})
              </span>
              <button
                onClick={() => { clearFilters(); setMobileOpen(false); }}
                className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
              >
                {t('clear_all')}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {activeFilterChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-soft-mint text-dark-teal rounded-md text-xs font-medium"
                >
                  {chip.label}
                  <button
                    onClick={chip.onRemove}
                    className="hover:bg-warm-gray rounded-full p-0.5 transition-colors"
                    aria-label={t('remove_filter', { filter: chip.label })}
                  >
                    <Icon name={ICONS.CLOSE} size="xs" color="text-dark-teal" ariaHidden />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desktop: inline chips bar */}
      <div
        className="hidden lg:flex items-center gap-2 px-4 py-3 bg-soft-mint text-dark-teal rounded-lg border border-light-teal-gray flex-wrap"
        role="status"
        aria-live="polite"
      >
        <Icon name={ICONS.FILTER} size="sm" color="text-dark-teal" ariaHidden />
        <span className="text-xs font-medium">
          {t('x_filters', { count: activeFilterChips.length })}
        </span>
        {chipsList}
        <Tooltip content={t('clear_all_filters')}>
          <button
            onClick={clearFilters}
            className="ml-auto px-2 py-1 hover:bg-off-white rounded text-xs font-medium transition-colors"
            aria-label={t('clear_all_filters')}
          >
            {t('clear_all')}
          </button>
        </Tooltip>
      </div>
    </>
  );
};
